/**
 * 搜索引擎 —— Swift SearchEngine.swift 的 TypeScript 镜像。
 * 评分规则、归一化、排序与 Swift 端完全对应（见 doc/46 4.2 节评分表）。
 */

import type { EncyclopediaEntry, EntryKind } from './models';
import { topic } from './moduleCatalog';

export interface SearchScope {
  kind?: EntryKind;
  moduleID?: string;
  topicID?: string;
}

/**
 * 归一化查询字符串，模拟 Swift 的
 * .folding(caseInsensitive + diacriticInsensitive + widthInsensitive, zh-Hans)。
 *
 * - NFKC：宽度归一化（全角→半角）。
 * - NFD + 去组合变音符：剥离重音。
 * - toLowerCase：大小写不敏感。
 * - trim：去首尾空白。
 */
export function normalize(value: string): string {
  return value
    .normalize('NFKC')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // 去组合变音符（重音）
    .toLowerCase()
    .trim();
}

function nameAscending(a: EncyclopediaEntry, b: EncyclopediaEntry): number {
  // 对应 Swift localizedStandardCompare，浏览器 Intl zh-Hans 排序。
  return a.nameZh.localeCompare(b.nameZh, 'zh-Hans');
}

function score(entry: EncyclopediaEntry, query: string): number {
  const nameZh = normalize(entry.nameZh);
  const nameEn = normalize(entry.nameEn);
  const aliases = entry.aliases.map(normalize);

  if (nameZh === query || nameEn === query) return 100;
  if (nameZh.startsWith(query) || nameEn.startsWith(query)) return 90;
  if (aliases.includes(query)) return 80;
  if (nameZh.includes(query) || nameEn.includes(query)) return 70;
  if (aliases.some((alias) => alias.includes(query))) return 60;
  if (normalize(entry.summary).includes(query)) return 40;
  if (
    entry.facts.some(
      (f) => normalize(f.label).includes(query) || normalize(f.value).includes(query),
    )
  ) {
    return 30;
  }
  if (
    entry.residentProfile?.giftTastes.some((g) =>
      g.items.some((item) => normalize(item).includes(query)),
    )
  ) {
    return 25;
  }
  if (
    entry.residentProfile?.universalGiftTastes?.some((g) =>
      g.items.some((item) => normalize(item).includes(query)),
    )
  ) {
    return 24;
  }
  if (entry.cropProfile) {
    const profile = entry.cropProfile;
    if (normalize(profile.seedName).includes(query)) {
      return 25;
    }
    const traits = [
      profile.regrowDays == null ? '不可复收' : '复收',
      profile.isTrellis ? '架作物 阻挡通行' : '',
      profile.isPaddyCrop ? '水稻类 靠近水源' : '',
      profile.needsWatering ? '需要浇水' : '无需浇水',
      profile.harvestMethod === 'scythe' ? '镰刀收获' : '手工采摘',
    ];
    if (traits.some((t) => normalize(t).includes(query))) {
      return 23;
    }
  }
  if (
    entry.sections.some(
      (s) => normalize(s.title).includes(query) || normalize(s.body).includes(query),
    )
  ) {
    return 20;
  }
  return 0;
}

export class SearchEngine {
  constructor(private readonly entries: readonly EncyclopediaEntry[]) {}

  search(query: string, scope: SearchScope = {}): EncyclopediaEntry[] {
    const candidates = this.entries.filter((entry) => {
      if (scope.kind && entry.kind !== scope.kind) return false;
      if (scope.topicID && entry.topicID !== scope.topicID) return false;
      if (scope.moduleID && topic(entry.topicID)?.moduleID !== scope.moduleID) {
        return false;
      }
      return true;
    });

    const normalizedQuery = normalize(query);
    if (normalizedQuery === '') {
      return [...candidates].sort(nameAscending);
    }

    return candidates
      .map((entry) => ({ entry, s: score(entry, normalizedQuery) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => {
        if (a.s !== b.s) return b.s - a.s;
        return nameAscending(a.entry, b.entry);
      })
      .map((x) => x.entry);
  }
}
